// UserMessage organism — ADR D-036, sprint chatgpt-user-bubble.
//
// The bubble is the AI Elements `Message`/`MessageContent` template as
// shipped: right-aligned, capped width (`max-w-user-bubble`, D1), tinted
// background (D2). Retires D57/D58's full-width override. A centred day
// + time line sits above every user message ("Hoy 17:50", "Ayer 09:12",
// "18 ago 17:50", D3); for non-founder origins the same line carries the
// sender chip. Founder messages get the brand-blue tint; wake-up /
// system / fleet messages keep the neutral grey so they never pass as
// Santi's (D2). No `position: sticky` (D4): the message scrolls away
// like any chat. No 3-line clamp (D5): the message shows in full. Edit
// / Resend are hover icons under the bubble (D6).

import type { ReactNode } from 'react';
import { useTranslation } from '../../i18n';
import { Icon } from '../atoms/Icon';
import type { MessageOrigin, StoredAttachment } from '../../types';
import { AttachmentChip } from '../AttachmentChip';
import { detectMdPaths } from '../../lib/detectMdPaths';
import { DocPathChip } from '../DocPathChip';
import { useDocViewer } from '../../hooks/useDocViewer';
import { formatMessageStamp } from '../../lib/format-message-stamp';
import { Message, MessageContent } from '../ai-elements/message';
import { MessageOriginChip } from './MessageOriginChip';
import { DeveloperOnly } from '../founder/DeveloperOnly';
import { isFounderOrigin } from '../../lib/messages';

// Exported since sprint machine-message-collapse (D4): the collapsed
// marker's popover renders the SAME text the bubble would, chips included.
export function renderTextWithChips(
  text: string,
  onOpen: (p: string) => void,
): ReactNode {
  const matches = detectMdPaths(text);
  if (matches.length === 0) return text;
  const parts: ReactNode[] = [];
  let cursor = 0;
  matches.forEach((m, i) => {
    if (m.start > cursor) parts.push(text.slice(cursor, m.start));
    parts.push(
      <DocPathChip
        key={`${m.start}-${m.path}-${i}`}
        path={m.path}
        onOpen={onOpen}
      />,
    );
    cursor = m.end;
  });
  if (cursor < text.length) parts.push(text.slice(cursor));
  return <>{parts}</>;
}

export interface UserMessageProps {
  text: string;
  onEdit?: (text: string) => void;
  onResend?: (text: string) => void;
  attachments?: StoredAttachment[] | null;
  agentId?: string;
  origin?: MessageOrigin | null;
  frame?: string | null;
  messageId?: string;
  timestamp?: string;
}

export function UserMessage({
  text,
  onEdit,
  onResend,
  attachments,
  agentId,
  origin,
  frame,
  messageId,
  timestamp,
}: UserMessageProps) {
  const { t, locale } = useTranslation();
  const { openDoc } = useDocViewer();
  const founder = isFounderOrigin(origin);
  const stamp = timestamp
    ? formatMessageStamp(timestamp, locale, {
        today: t('message.stamp.today'),
        yesterday: t('message.stamp.yesterday'),
      })
    : '';

  return (
    <div className="flex w-full flex-col gap-1.5" data-message-id={messageId}>
      {(stamp || !founder) && (
        <div
          className="flex w-full items-center justify-center gap-2 text-caption text-muted-foreground/70 [&>*]:mb-0"
          data-testid="user-message-stamp"
        >
          {!founder && <MessageOriginChip origin={origin} className="" />}
          {!founder && stamp && <span aria-hidden="true">·</span>}
          {stamp && <span>{stamp}</span>}
        </div>
      )}
      <Message from="user" className="relative max-w-user-bubble gap-0">
        <MessageContent
          className={`relative w-fit gap-0 rounded-2xl rounded-br-md ${
            founder
              ? 'group-[.is-user]:bg-status-info/20'
              : 'group-[.is-user]:bg-secondary'
          }`}
          data-testid="user-message"
        >
          {frame && (
            <DeveloperOnly>
              <div
                className="mb-2 text-caption text-muted-foreground/70 whitespace-pre-wrap break-words"
                data-testid="user-message-fleet-frame"
              >
                {frame}
              </div>
            </DeveloperOnly>
          )}
          {attachments && attachments.length > 0 && (
            <div
              className="mb-2 flex flex-wrap gap-2"
              data-testid="user-message-attachments"
            >
              {attachments.map((a) => (
                <AttachmentChip
                  key={a.id}
                  id={a.id}
                  route={a.route}
                  name={a.displayName}
                  size={a.size}
                  state="uploaded"
                  staticOnBubble
                  agentId={agentId}
                />
              ))}
            </div>
          )}
          <div className="text-chat leading-relaxed text-foreground whitespace-pre-wrap break-words">
            {renderTextWithChips(text, openDoc)}
          </div>
        </MessageContent>

        {(onEdit || onResend) && (
          <div
            className="absolute -bottom-8 right-0 flex items-center justify-end gap-0.5 opacity-0 transition-opacity duration-100 group-hover:opacity-100 focus-within:opacity-100"
            data-testid="user-message-actions"
          >
            {onEdit && (
              <button
                type="button"
                onClick={() => onEdit(text)}
                title={t('message.edit')}
                aria-label={t('message.edit')}
                className="inline-flex items-center rounded p-1.5 text-muted-foreground hover:bg-accent hover:text-foreground transition-colors duration-100"
              >
                <Icon name="Pencil" size={14} aria-hidden="true" />
              </button>
            )}
            {onResend && (
              <button
                type="button"
                onClick={() => onResend(text)}
                title={t('message.resend')}
                aria-label={t('message.resend')}
                className="inline-flex items-center rounded p-1.5 text-muted-foreground hover:bg-accent hover:text-foreground transition-colors duration-100"
              >
                <Icon name="CornerUpRight" size={14} aria-hidden="true" />
              </button>
            )}
          </div>
        )}
      </Message>
    </div>
  );
}
